/**
 * Chichewa catalog (Latin) for the Phase 2 `MessageId` set — Agent 9, batch 3.
 *
 * `dir: 'ltr'`. Chichewa / Nyanja (chiCheŵa) at a mathematician's register. **Warrant →
 * «Chitsimikizo»** (confirmation/warrant — flagged). Scientific loanwords are used where
 * Chichewa standardly borrows (logic → Lojiki, modal, heuristic, scalar, geometry). W/M grades
 * stay Latin (`::numbering-system/latn`). CLDR cardinal categories are one / other; the noun
 * uses the chi-/zi- class (choletsa → zoletsa). `frameActive` declares one/other plus an
 * explicit `=0`. Per INV-STABLE-ID a wrong translation is a presentation concern only — every
 * verdict stays fixed.
 */
import { P2 } from '../message-ids-phase2.js';
export const NY_CATALOG = {
    locale: 'ny',
    dir: 'ltr',
    messages: {
        [P2.inquiryTitle]: 'Kufufuza',
        [P2.sectionClaims]: 'Zonena',
        [P2.sectionIntegrity]: 'Kukhulupirika',
        [P2.axisWarrantLabel]: 'Chitsimikizo',
        [P2.axisWarrantValue]: 'W{w, number, ::numbering-system/latn}',
        [P2.axisEmpiricalLabel]: 'Zoyeserera',
        [P2.axisEmpiricalValue]: '{kind, select, defn {Tanthauzo} interp {Kumasulira} testable {Zoyesedwa} tested {Zayesedwa} refuted {Zakanidwa} program {Pulogalamu yofufuza} philos {Zafilosofi} other {Sizidziwika}}',
        [P2.axisModalLabel]: 'Modal',
        [P2.axisModalValue]: 'M{m, number, ::numbering-system/latn}',
        [P2.axisKidSummary]: 'Mlingo wamphamvu {w, number, ::numbering-system/latn}',
        [P2.stateExplorable]: 'Zofufuzika',
        [P2.stateRefuted]: 'Zakanidwa',
        [P2.stateOutside]: 'Kunja kwa malo',
        [P2.verdictConflict]: 'Zikutsutsana ndi {constraints}',
        [P2.verdictOutside]: 'Zikufuna {missing}',
        [P2.lossRegisterPlain]: 'Zafewetsedwa kukhala chinenero chodziwika — kulondola kwina kwasiyidwa.',
        [P2.lossRegisterKid]: 'Zafewetsedwa kwa owerenga aang’ono — tsatanetsatane wambiri wasiyidwa.',
        [P2.lossProfileDetail]: 'Mbiri yonse ya Chitsimikizo / Zoyeserera / Modal yafupikitsidwa pano.',
        [P2.lossConflictDetail]: 'Tsatanetsatane wa mkangano wafupikitsidwa pa mawonekedwe awa.',
        [P2.laneVerified]: 'Zatsimikiziridwa',
        [P2.laneUnverified]: 'Sizinatsimikiziridwe',
        [P2.laneConflict]: 'Mkangano watsimikiziridwa',
        [P2.constraintLogic]: 'Lojiki',
        [P2.constraintLightBends]: 'Kuwala kumakhotera m’munda wamphamvu yokoka',
        [P2.constraintParallelPostulate]: 'Postuleti ya mizere yofanana',
        [P2.claimScalarGravity]: 'Chiphunzitso cha scalar cha mphamvu yokoka',
        [P2.claimNonEuclidean]: 'Jiyometi yosakhala ya Euclid',
        [P2.claimSubstrateSr]: 'Kumasulira kwa substrate kwa ubale wapadera',
        [P2.edgeExtObservation]: 'Kuona kwakunja',
        [P2.edgeResourceSample]: 'Chuma chowerengera chogawana',
        [P2.edgeHeuristicSample]: 'Chitsogozo cha heuristic',
        [P2.edgeSharedSample]: 'Maziko ogawana',
        // --- Phase 2 view-chrome ---
        [P2.sectionConstraints]: 'Zoletsa',
        [P2.sectionEdges]: 'M’mphepete',
        [P2.sectionFrame]: 'Chimango',
        [P2.sectionExposure]: 'Kuwonetsedwa',
        [P2.statusAdoptedFormal]: 'Zavomerezedwa (mwalamulo)',
        [P2.statusAdoptedInterpretive]: 'Zavomerezedwa (mwakumasulira)',
        [P2.statusSuspended]: 'Zayimitsidwa',
        [P2.edgeEffectConstraint]: 'Zimapanga choletsa (zitha kukana)',
        [P2.edgeEffectResource]: 'Chuma — palibe chomwe chimakhudza ziweruzo',
        [P2.edgeEffectHeuristic]: 'Heuristic — palibe chomwe chimakhudza ziweruzo',
        [P2.edgeEffectShared]: 'Maziko ogawana — palibe chomwe chimakhudza ziweruzo',
        [P2.frameActive]: '{count, plural, =0 {Palibe zoletsa zogwira ntchito} one {# choletsa chogwira ntchito} other {# zoletsa zogwira ntchito}}',
        [P2.exposureNone]: 'Palibe kudalira kwakumasulira',
        [P2.exposureDep]: 'Zimadalira pa {id}',
        [P2.laneTrackMachine]: 'Makina',
        [P2.laneTrackExpert]: 'Katswiri',
        [P2.laneTrackCommunity]: 'Anthu',
        [P2.expertAudited]: 'Zawunikidwa',
        [P2.expertInProgress]: 'Zikuyenda',
        [P2.expertNotReviewed]: 'Sizinawunikidwe',
        [P2.communityFlags]: 'Pali zizindikiro',
        [P2.communityNone]: 'Palibe zizindikiro',
        [P2.controlChallenge]: 'Tsutsa',
        [P2.controlActivate]: 'Yatsa',
        [P2.controlMakeInterpretive]: 'Sandutsa wakumasulira',
        [P2.controlImportEdge]: 'Lowetsa m’mphepete',
        [P2.controlPromote]: 'Kweza',
        [P2.controlUndo]: 'Bwezera',
        [P2.controlRedo]: 'Chitanso',
        [P2.controlRegister]: 'Lembetsa',
        [P2.promoteRejected]: 'Kukwezedwa kwakanidwa',
        [P2.promoteAccepted]: 'Kukwezedwa kwavomerezedwa',
        [P2.promoteReasonRefutationTerminal]: 'Umboni sungachiritse kukana.',
        [P2.promoteReasonKeystone]: 'Kukwezedwa kofunika kumafuna chithandizo choyeserera.',
        [P2.promoteReasonEmpiricalNotFixed]: 'Mtundu woyeserera sunakhazikitsidwe.',
        [P2.promoteReasonModalNotFixed]: 'Mlingo wa modal sunakhazikitsidwe.',
        [P2.promoteReasonWarrantNotImproved]: 'Mlingo wa chitsimikizo sunawongolere.',
        [P2.evidenceComputation]: "{engine} computed {op} = {result} under {config}",
        [P2.lossNumericRounding]: "Result rounded to {dp} decimal places",
    },
};
//# sourceMappingURL=ny.js.map